import { Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { formatAmount } from '../../lib/constants';
import { useAmountsVisibility } from '../../context/AmountsVisibilityContext';
import { Skeleton } from '../ui/skeleton';

// Inicio's one strong brand block (redesign.md, Fase 4): what's left this
// period, spent vs. income, days left and a per-day spending guide. All the
// numbers come computed from GET /dashboard (days_left / daily_allowance use
// the same helper as the Coach); this only formats them.
//
// "Te queda" = ingresos − gastos − aportes a metas: money set aside in a goal
// isn't available to spend, so it isn't counted in the per-day guide either.
const WORDS = {
  monthly: { now: 'este mes', past: 'ese mes', until: 'hasta fin de mes' },
  weekly: { now: 'esta semana', past: 'esa semana', until: 'hasta el domingo' },
  daily: { now: 'hoy', past: 'ese día', until: '' },
};

export default function HomeSummary({ dashboard, loading = false }) {
  const { hidden, toggle } = useAmountsVisibility();

  if (loading || !dashboard) {
    return (
      <div className="rounded-[20px] brand-hero p-5 sm:p-6" data-testid="balance-card">
        <div data-testid="balance-loading" className="space-y-4">
          <Skeleton className="h-4 w-32 bg-on-brand/20" />
          <Skeleton className="h-11 w-44 bg-on-brand/20" />
          <Skeleton className="h-2 w-full bg-on-brand/20" />
          <Skeleton className="h-4 w-52 bg-on-brand/20" />
          <Skeleton className="h-12 w-full rounded-xl bg-on-brand/10" />
        </div>
      </div>
    );
  }

  const {
    total_income: income = 0, total_expenses: expenses = 0, total_goal_contributions: goals = 0,
    balance = 0, budget_period: period = 'monthly', is_current_period: isCurrent = true,
    days_left: daysLeft = 0, daily_allowance: perDay,
  } = dashboard;
  const words = WORDS[period] || WORDS.monthly;
  const ratio = income > 0 ? expenses / income : null;
  const money = (v) => formatAmount(v, hidden);

  let guide = null;
  if (isCurrent) {
    if (income === 0 && expenses === 0) {
      guide = { text: 'Anota tus ingresos para calcular cuánto puedes gastar al día.' };
    } else if (balance <= 0) {
      guide = {
        alert: true,
        text: balance < 0
          ? `Ya gastaste ${money(-balance)} más de lo que entró ${words.now}. Frena los gastos que no sean necesarios.`
          : `Ya no te queda margen ${words.now}. Frena los gastos que no sean necesarios.`,
      };
    } else if (period !== 'daily' && perDay != null) {
      guide = { perDay };
    }
  }

  const daysText = !isCurrent ? null
    : daysLeft <= 1 ? (period === 'daily' ? null : 'hoy es el último día')
    : `quedan ${daysLeft} días`;

  return (
    <section className="rounded-[20px] brand-hero text-on-brand p-5 sm:p-6" data-testid="balance-card">
      <button
        type="button"
        onClick={toggle}
        data-testid="toggle-amounts-visibility"
        title={hidden ? 'Mostrar montos' : 'Ocultar montos'}
        className="hit-44 inline-flex items-center gap-1.5 text-sm font-medium text-on-brand/80"
      >
        {isCurrent ? `Te queda ${words.now}` : `Te quedó ${words.past}`}
        {hidden ? <EyeOff className="w-4 h-4" strokeWidth={1.75} /> : <Eye className="w-4 h-4" strokeWidth={1.75} />}
      </button>
      <p className="mt-1 text-[2.75rem] sm:text-5xl font-semibold leading-none tracking-[-0.03em] metric-value" data-testid="balance-value">
        {balance < 0 && !hidden ? '−' : ''}{money(Math.abs(balance))}
      </p>

      {ratio !== null && (
        <div
          className="mt-5 h-2 w-full overflow-hidden rounded-full bg-on-brand/20"
          role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(Math.min(ratio, 1) * 100)}
          aria-label="Gastos sobre ingresos"
          data-testid="spent-ratio"
        >
          <div className="h-full rounded-full bg-on-brand budget-progress-bar" style={{ transform: `scaleX(${Math.min(ratio, 1)})` }} />
        </div>
      )}
      <p className="mt-2.5 text-sm text-on-brand/85" data-testid="spent-summary">
        {income > 0 ? <>Gastaste {money(expenses)} de {money(income)}</> : <>Gastaste {money(expenses)} · sin ingresos anotados</>}
        {daysText && <> · {daysText}</>}
      </p>
      {goals > 0 && (
        <p className="mt-0.5 text-xs text-on-brand/70" data-testid="goals-value">Apartaste {money(goals)} para metas</p>
      )}

      {/* The one element that pops inside the all-white-text brand block: a
          solid white card. Its background is white in both themes, so its
          text colors are fixed ink (not theme tokens) except the brand amount. */}
      {guide && guide.perDay != null && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-on-brand px-4 py-3 shadow-sm" data-testid="daily-allowance">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#1E1338]">Puedes gastar al día</p>
            {words.until && <p className="text-xs text-[#5B4E7A]">{words.until}</p>}
          </div>
          <p className="shrink-0 text-2xl font-bold tracking-tight text-brand metric-value">~{money(guide.perDay)}</p>
        </div>
      )}
      {guide && guide.text && (
        <div
          className={`mt-4 flex items-start gap-2 rounded-2xl bg-on-brand px-4 py-3 text-sm font-medium shadow-sm ${guide.alert ? 'text-[#9F1239]' : 'text-[#1E1338]'}`}
          data-testid={guide.alert ? 'daily-allowance-alert' : 'daily-allowance-hint'}
        >
          {guide.alert && <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.25} />}
          <span>{guide.text}</span>
        </div>
      )}
    </section>
  );
}
